<template>
  <TableComp
    v-if="stencils"
    :name="$t('global.stencils')"
    :headers="headers"
    :status="stencils.status"
    :pagination="stencils.pagination"
    :sort="stencils.sort"
    :number-of-items="stencils.list.length"
    empty-image="stencils"
    :empty-bold-message="$t('global.no-stencils-bold-message')"
    :empty-message="$t('global.no-stencils-message')"
    v-on:update:list="updateList"
    :header-actions="actions"
    :empty-actions="actions"
    :empty-help="help"
  >
    <!-- desktop table layout -->
    <template #desktop>
      <tr v-for="stencil in stencils.list" v-bind:key="stencil.id" class="text-center">
        <!-- stencil screenshot column -->
        <td>
          <div v-if="isLoading()" class="d-flex justify-content-center">
            <LoadingState :size="iconSize" />
          </div>
          <div v-else-if="isErrored()" class="d-flex justify-content-center">
            <ItemErrorState />
          </div>
          <span v-else>
            <LoadingState v-if="!stencil.screenshotUrl" :size="iconSize" />
            <img
              v-else
              :src="stencil.screenshotUrl"
              :width="screenshotSize"
              :height="screenshotSize"
            />
          </span>
        </td>

        <!-- stencil name column -->
        <td class="text-center">
          <div v-if="isLoading()" class="d-flex justify-content-center">
            <LoadingState :size="iconSize" />
          </div>
          <div v-else-if="isErrored()" class="d-flex justify-content-center">
            <ItemErrorState />
          </div>
          <span v-else>
            {{ stencil.name }}
          </span>
        </td>

        <!-- stencil's site domain column -->
        <td class="text-center">
          <div v-if="isLoading()" class="d-flex justify-content-center">
            <LoadingState :size="iconSize" />
          </div>
          <div v-else-if="isErrored()" class="d-flex justify-content-center">
            <ItemErrorState />
          </div>
          <span v-else>
            <router-link :to="getRouterLink(getSite(stencil))">
              {{ getSite(stencil).domain }}
            </router-link>
          </span>
        </td>

        <!-- stencil's creation date column -->
        <td class="text-center">
          <div v-if="isLoading()" class="d-flex justify-content-center">
            <LoadingState :size="iconSize" />
          </div>
          <div v-else-if="isErrored()" class="d-flex justify-content-center">
            <ItemErrorState />
          </div>
          <span v-else>
            {{ dateFormat(stencil.created, true) }}
          </span>
        </td>

        <!-- stencil's creation date column -->
        <td class="text-center">
          <div v-if="isLoading()" class="d-flex justify-content-center">
            <LoadingState :size="iconSize" />
          </div>
          <div v-else-if="isErrored()" class="d-flex justify-content-center">
            <ItemErrorState />
          </div>
          <span v-else>
            <Pill :variant="getStatusColor(stencil.state)" :content="stencil.state" />
          </span>
        </td>

        <!-- three dots menu column -->
        <td class="text-end actions">
          <div v-if="stencil.isStable" class="d-flex justify-content-end mr-4">
            <ThreeDotsMenu :items="menuItems(stencil)" />
          </div>
          <div v-else class="d-flex justify-content-center align-items-center">
            <div class="mr-2">
              <LoadingState :size="iconSize" />
            </div>
            <!-- <div class="text-capitalize">{{ stencil. }}...</div> -->
          </div>
        </td>
      </tr>
    </template>

    <!-- mobile table layout -->
    <template #mobile>
      <TableMobileRow v-for="stencil in stencils.list" v-bind:key="stencil.id">
        <template #header>
          <div class="d-flex align-items-center">
            <!-- stencil name column -->
            <div class="flex-fill">
              <div class="d-flex align-items-center">
                {{ stencil.name }}
              </div>
            </div>

            <!-- three dot menu on the left -->
            <div>
              <div v-if="stencil.isStable" class="d-flex justify-content-center">
                <ThreeDotsMenu :items="menuItems(stencil)" />
              </div>
              <div v-else class="d-flex justify-content-center align-items-center">
                <div class="mr-2">
                  <LoadingState :size="iconSize" />
                </div>
                <div class="text-capitalize">{{ stencil.state }}...</div>
              </div>
            </div>
          </div>
        </template>

        <template #content>
          <!-- stencil name -->
          <div class="row">
            <div class="col">{{ $t("global.name") }}:</div>
            <div class="col">
              <LoadingState v-if="isLoading()" :size="iconSize" />
              <item-error-state v-else-if="isErrored()" />
              <div v-else>
                {{ stencil.name }}
              </div>
            </div>
          </div>

          <!-- stencil's site domain -->
          <div class="row">
            <div class="col">{{ $t("global.domain") }}:</div>
            <div class="col">
              <LoadingState v-if="isLoading()" :size="iconSize" />
              <item-error-state v-else-if="isErrored()" />
              <router-link :to="getRouterLink(getSite(stencil))">
                {{ getSite(stencil).domain }}
              </router-link>
            </div>
          </div>

          <!-- stencil creation date -->
          <div class="row">
            <div class="col">{{ $t("global.created") }}:</div>
            <div class="col">
              <LoadingState v-if="isLoading()" :size="iconSize" />
              <item-error-state v-else-if="isErrored()" />
              <div v-else>
                {{ dateFormat(stencil.created, true) }}
              </div>
            </div>
          </div>

          <!-- stencil state -->
          <div class="row">
            <div class="col">{{ $t("global.state") }}:</div>
            <div class="col">
              <LoadingState v-if="isLoading()" :size="iconSize" />
              <item-error-state v-else-if="isErrored()" />
              <Pill v-else :variant="getStatusColor(stencil.state)" :content="stencil.state" />
            </div>
          </div>
        </template>
      </TableMobileRow>
    </template>
  </TableComp>
</template>

<script>
import constants from "@/utils/constants";
import { dateFormat } from "leatherman-js/utils/formatters/time";
import ItemErrorState from "@/components/display/ItemErrorState";
import LoadingState from "@/components/display/LoadingState";
import NavLink from "@/classes/NavLink";
import Pill from "@/components/display/Pill";
import StencilAddModal from "@/components/smart/modals/StencilAdd";
import StencilCreateSiteModal from "@/components/smart/modals/StencilCreateSite";
import StencilDeleteModal from "@/components/smart/modals/StencilDelete";
import store from "@/store";
import TableComp from "@/components/display/table/TableComp";
import TableHeader from "@/classes/TableHeader";
import TableMobileRow from "@/components/display/table/TableMobileRow";
import ThreeDotsMenu from "@/components/display/ThreeDotsMenu";

export default {
  name: "StencilsTable",
  props: {
    stencils: Object,
    plan: Object,
    sites: Array,
    sitesStatus: Object,
  },
  components: {
    ItemErrorState,
    LoadingState,
    TableComp,
    TableMobileRow,
    ThreeDotsMenu,
    Pill,
  },
  setup(props, { root }) {
    // table column headers for desktop version of site list table
    const headers = [
      new TableHeader(),
      new TableHeader(root.$t("global.name"), "justify-content-center"),
      new TableHeader(root.$t("global.site"), "justify-content-center"),
      new TableHeader(root.$t("global.created"), "justify-content-center"),
      new TableHeader(root.$t("global.status"), "justify-content-center"),
      new TableHeader(),
    ];

    const isLoading = () => props.sitesStatus.isLoading || props.stencils.status.isLoading;
    const isErrored = () => props.sitesStatus.isErrored || props.stencils.status.isErrored;
    const getSite = (stencil) => props.sites.find((s) => s.id === stencil.site.id);
    const getRouterLink = (site) => ({ name: "plan-site-detail", params: { siteId: site.id } });
    const getStatusColor = (state) => constants.STATUS_COLORS[state];

    // create a new stencil
    const createStencil = () => {
      store.dispatch("modals/open", {
        template: StencilAddModal,
        data: { sites: props.sites },
      });
    };

    // create a site based on a stencil
    const createSiteFromStencil = (stencil) => {
      store.dispatch("modals/open", {
        template: StencilCreateSiteModal,
        confirm: () => {
          // after site creation successfully started, redirect to the site list page
          root.$router.push({
            name: "plan-sites",
            params: {
              planId: props.plan.id,
            },
          });
        },
        data: {
          stencil,
          site: getSite(stencil),
          plan: props.plan,
        },
      });
    };

    // delete a stencil
    const deleteStencil = (stencil) => {
      store.dispatch("modals/open", {
        template: StencilDeleteModal,
        data: { stencil, site: getSite(stencil) },
      });
    };

    const actions = [
      new NavLink(root.$t("global.add-stencil"), "add", createStencil, false, null, "epsilon"),
    ];

    const help = [
      new NavLink(
        root.$t("global.learn-more"),
        null,
        () =>
          window.open(
            "https://help.nexcess.net/74095-wordpress/using-stencil-sites-in-managed-wordpress-and-managed-woocommerce-hosting",
            "_blank",
            "noopener, noreferrer"
          ),
        false,
        null,
        null
      ),
    ];

    // menu items for three dots menu on the right
    const menuItems = (stencil) => {
      const links = [
        // go to site dashboard
        new NavLink(root.$t("global.create-site-from-stencil"), null, () =>
          createSiteFromStencil(stencil)
        ),
        // open support ticket modal
        new NavLink(root.$t("global.delete"), null, () => deleteStencil(stencil), null, "danger"),
      ];

      return links;
    };

    const updateList = (event) => {
      const { pagination, sort } = event;
      store.dispatch("stencils/getList", {
        planId: props.plan.id,
        pageIndex: pagination?.index,
        pageSize: pagination?.size,
        sortProperty: sort?.property,
        sortDirection: sort?.direction,
      });
    };

    const iconSize = constants.TABLE_ICON_SIZE;

    const screenshotSize = 75;

    return {
      headers,
      help,
      menuItems,
      updateList,
      getRouterLink,
      getStatusColor,
      getSite,
      isLoading,
      isErrored,
      iconSize,
      dateFormat,
      actions,
      screenshotSize,
    };
  },
};
</script>
